import { all, takeEvery, put, call } from "@redux-saga/core/effects";
import { AnyAction } from "redux";
import { ActionResponseType } from "../../../common/types/common.types";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import { inspectionStandardQuestionnaireReducerActions } from "./InspectionStandardQuestionnaireTemplate.reducer";
import dayjs from "dayjs";
import store from "../../../core/store/index";
import { GET_INSPECTION_STANDARD_QUESTIONNAIRE_REPORT_DATA } from "./InspectionStandardQuestionnaireTemplate.actions";
import getInspectionStandardQuestionnaireReportApi from "./InspectionStandardQuestionnaireTemplate.api";
import {
  InspectionStandardDTO,
  InspectionStandardExcelDataResponseDTO,
} from "../../../common/types/common.dto.types";
import { setDownloadDataForInspectionStandard } from "../../pages/SiteInspectionStartPage/SiteInspectionStartPage.reducer";
import { InspectionStandardQuestionnaireDownloadStatusType } from "./InspectionStandardQuestionnaireTemplate.types";

const excelWorkerInspectionStandardQuestionnaire: Worker = new Worker(
  new URL("./ExcelWorkerInspectionStandardQuestionnaire.ts", import.meta.url)
);

excelWorkerInspectionStandardQuestionnaire.onmessage = (event) => {
  const excelData = event.data.buffer;

  //Handle the Excel data here, for example, you can save it as a file
  const blob = new Blob([excelData], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  let downloadFileName = `${event.data.name}`;
  const extension = getFileExtensionFromMimeType(blob.type);
  if (extension != null && extension != "") {
    if (!downloadFileName.endsWith(extension)) {
      downloadFileName += extension;
    }
  }
  // Create a download link for the Excel file and trigger the download
  const a = document.createElement("a");
  a.href = url;
  a.download = downloadFileName;
  a.click();

  window.URL.revokeObjectURL(url);
  store.dispatch(
    inspectionStandardQuestionnaireReducerActions.setQuestionnaireExcelReportStatus(
      {
        InspectionStandardId: event.data.InspectionStandardId,
        IsDownloading: false,
      } as InspectionStandardQuestionnaireDownloadStatusType
    )
  );
  store.dispatch(
    openPopup({
      Open: true,
      BodyText: event.data.isFirstDownload
        ? "Questionnaire downloaded successfully"
        : "Questionnaire re-downloaded successfully",
      HeaderText: "Success", //"Success",
      PopupType: EnumPopupType.SuccessPopup,
    } as PopupProps)
  );

  return () => {
    excelWorkerInspectionStandardQuestionnaire.terminate();
  };
};

const getInspectionStandardQuestionnaireReportWatcher = function* () {
  yield takeEvery(
    GET_INSPECTION_STANDARD_QUESTIONNAIRE_REPORT_DATA,
    function* (action: AnyAction): any {
      const data = action.payload as InspectionStandardDTO;

      yield put(
        inspectionStandardQuestionnaireReducerActions.setQuestionnaireExcelReportStatus(
          {
            InspectionStandardId: data.Id,
            IsDownloading: true,
          } as InspectionStandardQuestionnaireDownloadStatusType
        )
      );

      try {
        const result: ActionResponseType = yield call(() =>
          getInspectionStandardQuestionnaireReportApi.getInspectionStandardQuestionnaireReport(
            data
          )
        );
        if (result.IsSuccess) {
          const responseData: InspectionStandardExcelDataResponseDTO =
            result.Data;

          const inspectionStandard: InspectionStandardDTO =
            responseData.InspectionStandardDetails;

          const workerObj = {
            data: responseData?.ExcelData,
            name: `${inspectionStandard.Inspection?.Site?.SiteName} - ${
              inspectionStandard.Inspection?.Name
            } - ${inspectionStandard.Standard?.Name} - ${dayjs().format(
              "DD-MMM-YYYY HH_MM_ss_SSS"
            )}`,
            isFirstDownload: data.IsFirstDownload,
            inspectionStandardId: data.Id,
          };
          excelWorkerInspectionStandardQuestionnaire.postMessage(workerObj);
          yield put(
            setDownloadDataForInspectionStandard({
              inspectionStandardData: data,
              questionnaireAlreadyDownloaded: true,
            })
          );
        } else {
          yield put(
            openPopup({
              Open: true,
              BodyText:
                "Sorry, An error occured while generating the excel report!",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
              OkBtnClick() {},
            } as PopupProps)
          );
        }
      } catch {
        yield put(
          openPopup({
            Open: true,
            BodyText:
              "Sorry, An error occured while generating the excel report!",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
            OkBtnClick() {},
          } as PopupProps)
        );
      }

      // yield put(setLoading(false));
    }
  );
};

const getFileExtensionFromMimeType = (mimeType) => {
  const mimeTypesToExtensions = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      ".xlsx",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      ".docx",
    "application/pdf": ".pdf",
    "image/jpeg": ".jpg",
    "image/png": ".png",
  };
  return mimeTypesToExtensions[mimeType] || "";
};
const inspectionStandardQuestionnaireReportSaga = function* () {
  yield all([getInspectionStandardQuestionnaireReportWatcher()]);
};

export default inspectionStandardQuestionnaireReportSaga;
